import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { naira, StatusPill } from '@/components/affiliate/AffiliateWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GATEWAYS = ['paystack', 'flutterwave', 'korapay', 'squad', 'bachs'];
const LABELS = { paystack: 'Paystack', flutterwave: 'Flutterwave', korapay: 'Korapay', squad: 'Squad', bachs: 'BACHs' };
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const NUM_FIELDS = [
  ['min_amount', 'Minimum payout (₦)'], ['max_amount', 'Maximum payout (₦)'], ['daily_limit', 'Daily limit, all payouts (₦)'],
  ['monthly_limit', 'Monthly limit, all payouts (₦)'], ['conditional_auto_limit', 'Conditional: auto-pay up to (₦)'],
  ['conditional_daily_auto_limit', 'Conditional: daily auto total (₦)'], ['min_hours_between_payouts', 'Hours between payouts per affiliate'],
  ['max_payouts_per_month', 'Max payouts per affiliate / month'], ['allowed_start_hour', 'Allowed from hour (Lagos)'],
  ['allowed_end_hour', 'Allowed until hour (Lagos)'], ['bank_change_cooldown_hours', 'Hold after bank change (hours)'],
];

const SettingsForm = ({ headers }) => {
  const [s, setS] = useState(null);
  const load = useCallback(() => axios.get(`${API}/admin/payout-settings`, { headers }).then((r) => setS(r.data)), [headers]);
  useEffect(() => { load(); }, [load]);
  if (!s) return null;
  const save = async () => {
    const body = { ...s };
    delete body.gateway_status; delete body.updated_at;
    NUM_FIELDS.forEach(([k]) => { body[k] = Number(body[k]); });
    try { await axios.put(`${API}/admin/payout-settings`, body, { headers }); toast.success('Payout settings saved'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const test = async (slug) => {
    const { data } = await axios.post(`${API}/admin/payouts/gateways/${slug}/test`, {}, { headers });
    data.ok ? toast.success(`${LABELS[slug]}: ${data.message}`) : toast.error(`${LABELS[slug]}: ${data.message}`);
  };
  return (
    <div className="space-y-6" data-testid="payout-settings">
      <div className="flex flex-wrap gap-6 items-center p-4 rounded border bg-amber-50 border-amber-200">
        <label className="flex items-center gap-2 font-semibold"><Switch checked={s.payouts_enabled} onCheckedChange={(v) => setS({ ...s, payouts_enabled: v })} data-testid="payouts-enabled-switch" />Send real payouts</label>
        <p className="text-xs text-amber-800">While off, approved payouts wait in "scheduled". Test each gateway with sandbox keys before switching on.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label className="text-xs">Payout mode</Label>
          <Select value={s.mode} onValueChange={(v) => setS({ ...s, mode: v })}>
            <SelectTrigger data-testid="payout-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="manual">Manual - every payout needs approval</SelectItem><SelectItem value="automatic">Automatic - eligible payouts are sent</SelectItem><SelectItem value="conditional">Conditional - auto within limits, larger need approval</SelectItem></SelectContent>
          </Select></div>
        <div><Label className="text-xs">Default payout gateway</Label>
          <Select value={s.default_gateway} onValueChange={(v) => setS({ ...s, default_gateway: v })}>
            <SelectTrigger data-testid="default-gateway-select"><SelectValue /></SelectTrigger>
            <SelectContent>{GATEWAYS.map((g) => <SelectItem key={g} value={g}>{LABELS[g]}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div className="grid md:grid-cols-5 gap-3">
        {GATEWAYS.map((g) => (
          <div key={g} className="border rounded p-3 space-y-2" data-testid={`payout-gateway-${g}`}>
            <div className="flex justify-between items-center"><span className="font-semibold">{LABELS[g]}</span>
              <Switch checked={!!s.gateways[g]?.enabled} onCheckedChange={(v) => setS({ ...s, gateways: { ...s.gateways, [g]: { enabled: v } } })} data-testid={`payout-gateway-toggle-${g}`} /></div>
            <p className="text-xs text-gray-500">{s.gateway_status?.[g]?.configured ? `Keys set · ${s.gateway_status[g].environment}` : 'Payout key not set (Gateways tab)'}</p>
            <Button size="sm" variant="outline" className="w-full" onClick={() => test(g)} data-testid={`payout-gateway-test-${g}`}>Test connection</Button>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        {NUM_FIELDS.map(([k, l]) => <div key={k}><Label className="text-xs">{l}</Label><Input type="number" value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`payout-setting-${k}`} /></div>)}
      </div>
      <div className="flex gap-2 flex-wrap items-center"><span className="text-xs text-gray-500 mr-2">Allowed days:</span>
        {DAYS.map((d, i) => (
          <button key={d} type="button" onClick={() => setS({ ...s, allowed_days: s.allowed_days.includes(i) ? s.allowed_days.filter((x) => x !== i) : [...s.allowed_days, i].sort() })}
            className={`px-3 py-1 rounded-full text-xs border ${s.allowed_days.includes(i) ? 'bg-gray-900 text-white' : 'bg-white'}`} data-testid={`payout-day-${d}`}>{d}</button>
        ))}
      </div>
      <Button onClick={save} data-testid="payout-settings-save">Save payout settings</Button>
    </div>
  );
};

const PayoutActions = ({ p, headers, onDone }) => {
  const act = async (action, body = {}) => {
    try {
      const { data } = await axios.post(`${API}/admin/payouts/${p.id}/${action}`, body, { headers });
      toast.success(`Payout ${data.status.replace('_', ' ')}`);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || `${action} failed`); }
  };
  return (
    <div className="flex gap-1 flex-wrap">
      {p.status === 'pending_approval' && <Button size="sm" onClick={() => act('approve')} data-testid={`payout-approve-${p.id}`}>Approve</Button>}
      {['pending_approval', 'approved', 'scheduled'].includes(p.status) && <Button size="sm" variant="outline" onClick={() => { const r = window.prompt('Reason?'); if (r !== null) act('reject', { reason: r }); }} data-testid={`payout-reject-${p.id}`}>Reject</Button>}
      {['approved', 'scheduled'].includes(p.status) && <Button size="sm" variant="outline" onClick={() => act('process')} data-testid={`payout-process-${p.id}`}>Send now</Button>}
      {p.status === 'processing' && <Button size="sm" variant="outline" onClick={() => act('verify')} data-testid={`payout-verify-${p.id}`}>Verify</Button>}
      {p.status === 'failed' && <Button size="sm" variant="outline" onClick={() => act('retry')} data-testid={`payout-retry-${p.id}`}>Retry</Button>}
    </div>
  );
};

const CreatePayout = ({ headers, onDone }) => {
  const [affiliates, setAffiliates] = useState([]);
  const [form, setForm] = useState({ affiliate_id: '', amount: '', gateway: '' });
  useEffect(() => { axios.get(`${API}/admin/affiliates`, { headers }).then((r) => setAffiliates(r.data)); }, [headers]);
  const submit = async () => {
    try {
      await axios.post(`${API}/admin/payouts`, { affiliate_id: form.affiliate_id, amount: Number(form.amount), gateway: form.gateway || null }, { headers });
      toast.success('Payout created'); setForm({ affiliate_id: '', amount: '', gateway: '' }); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not create payout'); }
  };
  return (
    <div className="grid md:grid-cols-4 gap-3 items-end" data-testid="create-payout-form">
      <Select value={form.affiliate_id} onValueChange={(v) => setForm({ ...form, affiliate_id: v })}>
        <SelectTrigger data-testid="create-payout-affiliate"><SelectValue placeholder="Affiliate" /></SelectTrigger>
        <SelectContent>{affiliates.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · balance {naira(a.stats.available_balance)}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="number" placeholder="Amount (₦)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="create-payout-amount" />
      <Select value={form.gateway} onValueChange={(v) => setForm({ ...form, gateway: v })}>
        <SelectTrigger data-testid="create-payout-gateway"><SelectValue placeholder="Default gateway" /></SelectTrigger>
        <SelectContent>{GATEWAYS.map((g) => <SelectItem key={g} value={g}>{LABELS[g]}</SelectItem>)}</SelectContent>
      </Select>
      <Button onClick={submit} disabled={!form.affiliate_id || !form.amount} data-testid="create-payout-submit">Create payout</Button>
    </div>
  );
};

export default function PayoutsManagement() {
  const { token } = useAuth();
  const [headers] = useState({ Authorization: `Bearer ${token}` });
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    const [p, s, l] = await Promise.all([
      axios.get(`${API}/admin/payouts`, { headers, params: filter === 'all' ? {} : { status: filter } }),
      axios.get(`${API}/admin/payouts/summary`, { headers }), axios.get(`${API}/admin/audit-logs`, { headers })]);
    setPayouts(p.data); setSummary(s.data); setLogs(l.data);
  }, [headers, filter]);
  useEffect(() => { load().catch(() => toast.error('Failed to load payouts')); }, [load]);

  const st = summary?.by_status || {};
  return (
    <div className="space-y-6" data-testid="payouts-management">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[['Awaiting approval', st.pending_approval], ['Scheduled', st.scheduled], ['Processing', st.processing], ['Successful', st.successful], ['Failed', st.failed]].map(([l, v]) => (
          <Card key={l}><CardContent className="pt-4"><p className="text-xs text-gray-500">{l}</p><p className="text-lg font-bold" data-testid={`payout-summary-${l.toLowerCase().replace(/\s/g, '-')}`}>{v?.count || 0} · {naira(v?.amount)}</p></CardContent></Card>
        ))}
        <Card><CardContent className="pt-4"><p className="text-xs text-gray-500">Fees paid</p><p className="text-lg font-bold">{naira(st.successful?.fees)}</p></CardContent></Card>
      </div>
      {summary?.needs_reconciliation?.length > 0 && (
        <div className="p-3 rounded border border-red-200 bg-red-50 text-sm" data-testid="reconciliation-alert">
          {summary.needs_reconciliation.length} payout(s) processing for over 1 hour - use "Verify" to reconcile with the provider.
        </div>
      )}
      <Card><CardHeader><CardTitle>Payout settings</CardTitle></CardHeader><CardContent><SettingsForm headers={headers} /></CardContent></Card>
      <Card><CardHeader><CardTitle>Create a payout</CardTitle></CardHeader><CardContent><CreatePayout headers={headers} onDone={load} /></CardContent></Card>
      <Card>
        <CardHeader className="flex flex-row justify-between items-center"><CardTitle>Payouts</CardTitle>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="payout-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{['all', 'pending_approval', 'approved', 'scheduled', 'processing', 'successful', 'failed', 'rejected'].map((x) => <SelectItem key={x} value={x}>{x.replace('_', ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payouts-table">
            <thead><tr className="text-left text-gray-500 border-b"><th>Date</th><th>Affiliate</th><th>Amount</th><th>Fee</th><th>Gateway</th><th>Destination</th><th>Reference</th><th>Status</th><th>Reason / note</th><th></th></tr></thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-b align-top" data-testid={`payout-row-${p.id}`}>
                  <td className="py-2">{new Date(p.created_at).toLocaleString()}</td><td>{p.affiliate_code}</td><td className="font-semibold">{naira(p.amount)}</td><td>{naira(p.fee)}</td>
                  <td>{LABELS[p.gateway]}{p.auto_approved && <span className="block text-xs text-gray-500">auto</span>}</td>
                  <td>{p.destination?.bank_name}<span className="block text-xs text-gray-500">{p.destination?.account_name} ****{(p.destination?.account_number || '').slice(-4)}</span></td>
                  <td className="font-mono text-xs">{p.reference}<span className="block">{p.provider_reference}</span></td>
                  <td><StatusPill status={p.status} /></td><td className="text-xs max-w-xs">{p.failure_reason || p.note || '-'}</td>
                  <td><PayoutActions p={p} headers={headers} onDone={load} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!payouts.length && <p className="text-sm text-gray-500 py-4 text-center">No payouts</p>}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Audit log</CardTitle></CardHeader>
        <CardContent className="max-h-80 overflow-y-auto">
          <table className="w-full text-xs" data-testid="audit-log-table"><tbody>
            {logs.map((l) => <tr key={l.id} className="border-b"><td className="py-1 pr-2 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td><td className="pr-2">{l.actor}</td><td className="pr-2 font-semibold">{l.action}</td><td className="text-gray-500 break-all">{JSON.stringify(l.details).slice(0, 160)}</td></tr>)}
          </tbody></table>
        </CardContent>
      </Card>
    </div>
  );
}
